<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="tree-context-menu-overlay"
      @mousedown.self="onOverlayMouseDown"
    >
      <ul
        ref="menuRef"
        class="tree-context-menu"
        role="menu"
        :style="menuStyle"
        @keydown="onKeyDown"
      >
        <li v-for="(item, idx) in items" :key="item.id" role="none">
          <button
            :data-index="idx"
            class="tree-context-menu-item"
            role="menuitem"
            type="button"
            :disabled="item.disabled"
            @click="selectItem(item)"
          >
            <span class="tree-context-menu-item-label">{{ item.label }}</span>
            <span v-if="item.shortcut" class="tree-context-menu-item-shortcut">{{ item.shortcut }}</span>
          </button>
        </li>
      </ul>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, watch, nextTick, computed } from 'vue';

export interface ContextMenuItem {
  id: string;
  label: string;
  disabled?: boolean;
  shortcut?: string;
}

interface Props {
  items: ContextMenuItem[];
  x: number;
  y: number;
  visible: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'select', id: string): void;
  (e: 'close'): void;
}>();

const menuRef = ref<HTMLElement | null>(null);
const currentIndex = ref(0);

const menuStyle = computed(() => ({ left: props.x + 'px', top: props.y + 'px' }));

function focusIndex(idx: number) {
  const buttons = menuRef.value?.querySelectorAll<HTMLButtonElement>('button.tree-context-menu-item');
  if (!buttons || !buttons.length) return;
  const clamped = Math.max(0, Math.min(idx, buttons.length - 1));
  currentIndex.value = clamped;
  nextTick(() => buttons[clamped]?.focus());
}

function onKeyDown(e: KeyboardEvent) {
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      focusIndex(currentIndex.value + 1);
      break;
    case 'ArrowUp':
      e.preventDefault();
      focusIndex(currentIndex.value - 1);
      break;
    case 'Home':
      e.preventDefault();
      focusIndex(0);
      break;
    case 'End':
      e.preventDefault();
      focusIndex(Number.MAX_SAFE_INTEGER);
      break;
    case 'Enter':
    case ' ': {
      e.preventDefault();
      const item = props.items[currentIndex.value];
      if (item && !item.disabled) selectItem(item);
      break; }
    case 'Escape':
      e.preventDefault();
      emit('close');
      break;
  }
}

function selectItem(item: ContextMenuItem) {
  emit('select', item.id);
}

function onGlobalClick(e: MouseEvent) {
  // If click outside menu, close
  if (!menuRef.value) return;
  if (!menuRef.value.contains(e.target as Node)) {
    emit('close');
  }
}

function onOverlayMouseDown() {
  emit('close');
}

function onGlobalContext(e: MouseEvent) {
  // Prevent nested context menu while ours visible
  if (props.visible) {
    if (!menuRef.value?.contains(e.target as Node)) {
      emit('close');
    }
  }
}

watch(() => props.visible, (v) => {
  if (v) {
    nextTick(() => focusIndex(0));
  }
});

onMounted(() => {
  window.addEventListener('mousedown', onGlobalClick, true);
  window.addEventListener('contextmenu', onGlobalContext, true);
});

onBeforeUnmount(() => {
  window.removeEventListener('mousedown', onGlobalClick, true);
  window.removeEventListener('contextmenu', onGlobalContext, true);
});
</script>

<style scoped lang="scss">
.tree-context-menu-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: none;
}

.tree-context-menu {
  position: absolute;
  margin: 0;
  padding: .15rem;
  list-style: none;
  min-width: 170px;
  background: var(--vscode-menu-background, var(--vscode-dropdown-background, var(--vscode-editorWidget-background)));
  border: 1px solid var(--vscode-menu-border, var(--vscode-dropdown-border, var(--vscode-widget-border)));
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
  font-size: 12px;
  color: var(--vscode-menu-foreground, var(--vscode-dropdown-foreground, var(--vscode-foreground)));
  z-index: 10000;
  pointer-events: auto;
}

.tree-context-menu-item {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: .15rem;
  padding: .5rem;
  border-radius: 4px;
  background: transparent;
  border: none;
  text-align: left;
  color: inherit;
  cursor: pointer;
  font: inherit;
  line-height: 1.3;

  // Unified hover + keyboard focus style: remove blue border per request
  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    background: var(--vscode-list-hoverBackground);
    outline: none !important;
    box-shadow: none !important;
  }
  // Extra hard reset in case upstream styles apply outline via :focus
  &:focus { outline: none !important; box-shadow: none !important; }
  &:disabled { opacity: .4; cursor: default; }
}

.tree-context-menu-item-shortcut {
  opacity: .6;
  font-size: 11px;
}
</style>
